/** @format */

/**
 * Sanitizes a phone number by removing all non-numeric characters
 * @param {string|number} phoneNumber - The phone number to sanitize
 * @returns {string} - The sanitized phone number containing only digits
 */
export const sanitizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Convert to string and remove all non-numeric characters
  return phoneNumber.toString().replace(/\D/g, '');
};

/**
 * Formats a phone number in US format (XXX) XXX-XXXX
 * @param {string|number} phoneNumber - The phone number to format
 * @returns {string} - The formatted phone number or original if invalid
 */
export const formatPhoneNumber = (phoneNumber) => {
  const sanitized = sanitizePhoneNumber(phoneNumber);
  
  // Check if it's a valid US phone number (10 digits)
  if (sanitized.length === 10) {
    return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6)}`;
  }
  
  // Check if it's a valid US phone number with country code (11 digits starting with 1)
  if (sanitized.length === 11 && sanitized.startsWith('1')) {
    const phoneWithoutCountryCode = sanitized.slice(1);
    return `(${phoneWithoutCountryCode.slice(0, 3)}) ${phoneWithoutCountryCode.slice(3, 6)}-${phoneWithoutCountryCode.slice(6)}`;
  }
  
  // Return original if it doesn't match expected patterns
  return phoneNumber.toString();
};

/**
 * Validates if a phone number is valid (10 digits)
 * @param {string|number} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhoneNumber = (phoneNumber) => {
  const sanitized = sanitizePhoneNumber(phoneNumber);
  return sanitized.length === 10 || (sanitized.length === 11 && sanitized.startsWith('1'));
};

/**
 * Sanitizes phone number for form input (keeps only digits and limits to 10 digits)
 * @param {string|number} phoneNumber - The phone number to sanitize for input
 * @returns {string} - The sanitized phone number for form input
 */
export const sanitizePhoneNumberForInput = (phoneNumber) => {
  const sanitized = sanitizePhoneNumber(phoneNumber);
  
  // Limit to 10 digits for US phone numbers
  return sanitized.slice(0, 10);
};